import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { profileModel, shopModel } from '../Axios/AxiosConfig';
import { toast, Zoom } from "react-toastify";
import MedicineRecommendations from '../AI Suggestions/MedicineRecommendation';
import { useNavigate, useNavigation } from 'react-router-dom';
import { ayurvedaAssessments } from '../Prescription/ayurvedaAssessments';

function cleanJsonString(rawString) {
    return rawString
        .replace(/```json\s*/g, "")
        .replace(/```\s*/g, "")
        .trim();
}

const ayurvedaFields = {
    Agni: {
        options: ["Mandagni", "Vishamagni", "Samagni", "Tikshnagni"]
    },
    Manas: {
        options: ["Pravara", "Madhyama", "Avara"]
    },
    Nidra: {
        options: ["Normal", "Alpa", "Atinidra"]
    },
    Koshtam: {

        options: ["Mrdu", "Madhyama", "Krura"]
    },
    Malam: {

        options: ["Ama-avastha", "Nirama-avastha"]
    },
    Mutram: {

        options: ["Vata", "Pitta", "Kapha"]
    },
    Vyayamam: {

        options: ["Uttama", "Madhyama", "Alpa"]
    },
    Vyadhiavastha: {

        options: ["Asukari", "Chirakari"]
    }
};

const getPreferredOrder = (param) => ayurvedaFields?.[param]?.options ?? [];

const majorityFromQuestions = (param, questions = []) => {
    const counts = new Map();
    const firstSeenOrder = [];

    questions.forEach(q => {
        const v = q?.answer ?? q?.selected ?? q?.value;
        if (!v) return;
        if (!counts.has(v)) {
            counts.set(v, 0);
            firstSeenOrder.push(v);
        }
        counts.set(v, counts.get(v) + 1);
    });

    if (counts.size === 0) return ""; // nothing selected

    let max = -1;
    for (const c of counts.values()) max = Math.max(max, c);
    const candidates = [...counts.entries()].filter(([, c]) => c === max).map(([v]) => v);

    if (candidates.length === 1) return candidates[0];

    // tie-break by ayurvedaFields order, else first-seen
    const pref = getPreferredOrder(param);
    if (pref.length) {
        for (const opt of pref) if (candidates.includes(opt)) return opt;
    }
    for (const v of firstSeenOrder) if (candidates.includes(v)) return v;
    return candidates[0];
};

const usePrescriptionStore = create(
    persist(
        (set, get) => ({
            selectedPatientdetails: null,
            selectedAppointemntdetails: null,
            audioBase64: null,
            selectedPrescription: null,

            setSelectedPatientdetails: (dataOrUpdater) =>
                set((state) => ({
                    selectedPatientdetails:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.selectedPatientdetails)
                            : dataOrUpdater,
                })),

            setSelectedAppointemntdetails: (data) => set({ selectedAppointemntdetails: data }),
            setAudioBase64: (data) => set({ audioBase64: data }),
            setPrescription: (data) => set({ selectedPrescription: data }),

            openRecipeAI: false,
            recipeLoading: false,
            recipeAIRecommendation: [],
            selectedRecipes: [],


            familyHistory: [
                { relation: 'Father', disease: '', duration: '' },
                { relation: 'Mother', disease: '', duration: '' }
            ],

            metrics: {
                bloodPressure: "",
                randomBloodSugar: "",
                pulseRate: "",
                oximeterValue: "",
                respiratoryRate: "",
            },

            formData: {},

            customMetrics: [],
            isEditingResult: null,  // holds parameter name or null
            editValue: "",
            labTests: [],
            FoodtobeTaken: [],
            Foodtobeavoided: [],
            yogalist: {},
            meditationList: {},
            pranayama: {},
            lifestylechanges: "",
            FollowUp: "No",
            symptoms: [],
            diseases: [],
            lifestylePractices: [],
            Prakriti_details: null,
            patientProfile: null,
            herbalProducts: [],
            AllDiet: {},
            AllrawDiet: [],
            AllRecipes: [],
            Recipes: [],
            Practices: [],
            transcript: "",
            error: null,
            includedCategories: [],
            avoidedCategories: [],
            creationStartTime: null,
            lifestylePracticesLoading: false,
            medicinerecommendation: [],
            MedicineRecommendationLoading: false,
            aiParameters: [],
            doshaAIdiagnois: null,

            Dosham: {
                Increased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
                Decreased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
            },

            Ayurvedaparameters: Object.values(ayurvedaAssessments).map(assessment => ({
                ...assessment,
                value: '',
                questions: assessment.questions.map(q => ({ ...q, answer: '' }))
            })),

            currentDrugs: [{
                medicine: "",
                dose: "",
                duration: "",
            }],

            addCurrentdrugs: () =>
                set((state) => ({
                    currentDrugs: [
                        ...(state.currentDrugs ?? []),
                        { medicine: "", dose: "", duration: "" },
                    ],
                })),

            removeCurrentdrugs: (index) => {
                set((state) => ({
                    currentDrugs: state.currentDrugs.filter((_, i) => i !== index),
                }));
            },



            setAyurvedaparameters: (data) => set({ Ayurvedaparameters: data }),

            handleAnswerSelect: (parameter, questionId, value) =>
                set((state) => {
                    const norm = (s) => (s ?? "").toString().trim().toLowerCase();

                    const updated = (state.Ayurvedaparameters ?? []).map((item) => {
                        if (norm(item?.parameter) !== norm(parameter)) return item;

                        const qs = Array.isArray(item?.questions) ? item.questions : [];
                        const updatedQuestions = qs.map((q) =>
                            q?.id === questionId ? { ...q, answer: value } : q
                        );

                        return {
                            ...item,
                            questions: updatedQuestions,
                            value: majorityFromQuestions(item?.parameter, updatedQuestions) || "",
                        };
                    });

                    return { Ayurvedaparameters: updated };
                }),

            handleEditResult: (paramObj) => {
                set({
                    isEditingResult: paramObj?.parameter ?? null,
                    editValue: paramObj?.value ?? "",
                });
            },

            setEditValue: (v) => set({ editValue: v }),

            handleSaveResult: (parameterName) => {
                const { Ayurvedaparameters, editValue } = get();
                if (!Array.isArray(Ayurvedaparameters)) {
                    return set({ isEditingResult: null, editValue: "" });
                }

                const norm = (s) => (s ?? "").toString().trim().toLowerCase();
                const targetParam = (Ayurvedaparameters.find(p => norm(p?.parameter) === norm(parameterName)) ?? {}).parameter;

                const allowed = ayurvedaFields?.[targetParam]?.options;
                const nextValue = (allowed && allowed.length && !allowed.includes(editValue))
                    ? editValue
                    : editValue;

                let changed = false;

                const updated = Ayurvedaparameters.map(p => {
                    if (norm(p?.parameter) !== norm(parameterName)) return p;
                    if ((p?.value ?? "") === nextValue) return p;
                    changed = true;
                    return { ...p, value: nextValue };
                });

                set({
                    Ayurvedaparameters: changed ? updated : Ayurvedaparameters,
                    isEditingResult: null,
                    editValue: "",
                });
            },

            cancelEditResult: () => set({ isEditingResult: null, editValue: "" }),

            setIsEditingResult: (param) => set({ isEditingResult: param }),

            Medicineinputs: [
                {
                    medicine: "",
                    Reason: "",
                    dose: "",
                    instruction: "",
                    duration: "",
                    quantity: 1,
                    id: "",
                    timing: "",
                },
            ],

            Medicaldetails: {
                Complaints: "",
                lab: "",
                history: "",
                drugs: "",
                ayurvedadiagnosis: "",
                moderndiagnosis: "",
                diagnosis: "",
            },

            setcurrentDrugs: (dataOrUpdater) =>
                set((state) => ({
                    currentDrugs:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.currentDrugs)
                            : dataOrUpdater,
                })),

            setdoshaAIdiagnois: (dataOrUpdater) =>
                set((state) => ({
                    doshaAIdiagnois:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.doshaAIdiagnois)
                            : dataOrUpdater,
                })),

            setFamilyHistory: (dataOrUpdater) =>
                set((state) => ({
                    familyHistory:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.familyHistory)
                            : dataOrUpdater,
                })),

            setMedicaldetails: (dataOrUpdater) =>
                set((state) => ({
                    Medicaldetails:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.Medicaldetails)
                            : dataOrUpdater,
                })),

            setMedicineinputs: (dataOrUpdater) =>
                set((state) => ({
                    Medicineinputs:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.Medicineinputs)
                            : dataOrUpdater,
                })),

            handleAyurvedaAnswerSelect: (parameterName, questionId, selectedValue) => {
                set(state => {
                    const updatedParams = state.Ayurvedaparameters.map(param => {
                        if (param.parameter === parameterName) {
                            const updatedQuestions = param.questions.map(q =>
                                q.id === questionId ? { ...q, answer: selectedValue } : q
                            );

                            const answers = updatedQuestions.map(q => q.answer).filter(Boolean);
                            let dominantValue = '';
                            if (answers.length > 0) {
                                const valueCounts = answers.reduce((acc, value) => {
                                    acc[value] = (acc[value] || 0) + 1;
                                    return acc;
                                }, {});
                                dominantValue = Object.keys(valueCounts).reduce((a, b) =>
                                    valueCounts[a] > valueCounts[b] ? a : b
                                );
                            }
                            return {
                                ...param,
                                questions: updatedQuestions,
                                value: dominantValue,
                            };
                        }
                        return param;
                    });
                    return { Ayurvedaparameters: updatedParams };
                });
            },

            handleSaveAyurvedaResult: (parameterName, editValue) => {
                set(state => ({
                    Ayurvedaparameters: state.Ayurvedaparameters.map(p =>
                        p.parameter === parameterName
                            ? { ...p, value: editValue }
                            : p
                    )
                }));
            },

            setPrakritiDetails: (dataOrUpdater) =>
                set((state) => ({
                    Prakriti_details:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.Prakriti_details)
                            : dataOrUpdater,
                })),

            setDosham: (dataOrUpdater) =>
                set((state) => ({
                    Dosham:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.Dosham)
                            : dataOrUpdater,
                })),

            HandleDosham: (type, dosha, isChecked) => {

                const { setDosham } = get();

                setDosham((prev) => {
                    const section = prev?.[type];
                    if (!section || !Array.isArray(section.doshas)) {
                        console.error(`Invalid Dosham type or doshas not array for: ${type}`);
                        return prev;
                    }

                    const alreadySelected = section.doshas.includes(dosha);
                    const updatedDoshas = isChecked
                        ? alreadySelected
                            ? section.doshas
                            : [...section.doshas, dosha]
                        : section.doshas.filter((d) => d !== dosha);

                    return {
                        ...prev,
                        [type]: {
                            ...section,
                            doshas: updatedDoshas,
                        },
                    };
                });

            },

            updateDoshaPercentage: (type, dosha, value) => {
                const { setDosham } = get();
                setDosham((prev) => ({
                    ...prev,
                    [type]: {
                        ...prev[type],
                        percentage: {
                            ...prev[type].percentage,
                            [dosha]: value,
                        },
                    },
                }));
            },

            updateDoshaIntensity: (type, dosha, value) => {
                const { setDosham } = get();
                setDosham((prev) => ({
                    ...prev,
                    [type]: {
                        ...prev[type],
                        intensity: {
                            ...prev[type].intensity,
                            [dosha]: value,
                        },
                    },
                }));
            },

            setAiParameters: (dataOrUpdater) =>
                set((state) => ({
                    formData:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.aiParameters)
                            : dataOrUpdater,
                })),

            setFormData: (dataOrUpdater) =>
                set((state) => ({
                    formData:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.formData)
                            : dataOrUpdater,
                })),

            setMetrics: (dataOrUpdater) =>
                set((state) => ({
                    metrics:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.metrics)
                            : dataOrUpdater,
                })),

            setCustomMetrics: (dataOrUpdater) =>
                set((state) => ({
                    customMetrics:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.customMetrics)
                            : dataOrUpdater,
                })),

            setlifestylepractices: (dataOrUpdater) => {
                set((state) => ({
                    lifestylePractices:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.lifestylePractices)
                            : dataOrUpdater,
                }));
            },

            getLifestylePracticesRecommendations: async (UID) => {
                try {
                    set({ lifestylePracticesLoading: true });

                    const {
                        selectedPatientdetails,
                        diseases,
                        Dosham,
                        Prakriti_details,
                        symptoms
                    } = get();

                    const getPrakritiDoshas = (prakriti) => {
                        if (!prakriti) return [];

                        const scores = [
                            { name: "Vata", value: Number(prakriti?.vata) || 0 },
                            { name: "Pitta", value: Number(prakriti?.pitta) || 0 },
                            { name: "Kapha", value: Number(prakriti?.kapha) || 0 }
                        ].sort((a, b) => b.value - a.value);

                        const doshas = [];

                        if (scores[0]?.value > 0) {
                            doshas.push(scores[0].name);
                        }

                        if (
                            scores[1]?.value > 0 &&
                            Math.abs(scores[0].value - scores[1].value) < 15
                        ) {
                            doshas.push(scores[1].name);
                        }

                        return doshas;
                    };

                    const payload = {
                        UID: UID || selectedPatientdetails?.UID || "",
                        id: UID || selectedPatientdetails?.UID || "",
                        age: selectedPatientdetails?.dob || "",
                        gender: selectedPatientdetails?.gender || "unknown",
                        chief_complaints: Array.isArray(symptoms)
                            ? symptoms.map((e) => e?.symptoms).filter(Boolean)
                            : [],
                        symptoms: Array.isArray(symptoms)
                            ? symptoms
                                .map((e) => e?.symptoms)
                                .filter(Boolean)
                                .join(", ")
                            : "",
                        disease: Array.isArray(diseases)
                            ? diseases
                                .map((e) => {
                                    const ayurvedaDisease = e?.ayurvedaDisease || "";
                                    const modernDisease = e?.modernDisease || "";
                                    return ayurvedaDisease || modernDisease
                                        ? `${ayurvedaDisease}${modernDisease ? ` (${modernDisease})` : ""}`
                                        : null;
                                })
                                .filter(Boolean)
                                .join(", ")
                            : "none",
                        vikriti_dosha: Array.isArray(Dosham?.Increased?.doshas)
                            ? Dosham.Increased.doshas
                            : [],
                        vikriti_doshas: Array.isArray(Dosham?.Increased?.doshas)
                            ? Dosham.Increased.doshas
                            : [],
                        prakriti_dosha: getPrakritiDoshas(Prakriti_details),
                        prakriti_doshas: getPrakritiDoshas(Prakriti_details)
                    };

                    console.log("Fetching lifestyle recommendations with payload:", payload);

                    const res = await profileModel.post("/getLifestyleRecommendations", payload);

                    if (res?.status === 200 && res?.data) {
                        const rawAnalysis = res.data.analysis || res.data;

                        let parsedData = {};

                        try {
                            if (typeof rawAnalysis === "string") {
                                parsedData = JSON.parse(cleanJsonString(rawAnalysis));
                            } else {
                                parsedData = rawAnalysis;
                            }
                        } catch (parseError) {
                            console.log("Error parsing lifestyle analysis:", parseError);
                            parsedData = {};
                        }

                        const normalizedData = {};

                        if (parsedData && typeof parsedData === "object" && !Array.isArray(parsedData)) {
                            Object.keys(parsedData).forEach((key) => {
                                const titleCaseKey =
                                    key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();

                                const value = parsedData[key];

                                normalizedData[titleCaseKey] = Array.isArray(value)
                                    ? value.map((practice) => ({
                                        ...practice,
                                        id: practice?.id || practice?.Id || "",
                                        Id: practice?.Id || practice?.id || ""
                                    }))
                                    : value;
                            });
                        }

                        console.log("normalizedData", normalizedData);
                        set({ lifestylePractices: normalizedData });
                        console.log("Lifestyle practices fetched successfully:", normalizedData);
                    } else {
                        set({ lifestylePractices: {} });
                        console.log("No valid lifestyle practices data received");
                    }
                } catch (error) {
                    console.log("Error fetching lifestyle practices:", error);
                    set({ lifestylePractices: {} });
                } finally {
                    set({ lifestylePracticesLoading: false });
                }
            },

            getMedicineRecommendations: async () => {
                try {
                    const { selectedPatientdetails, symptoms } = get();

                    if (!symptoms || symptoms.length === 0) {
                        toast.error("Please enter chief complaints before proceeding", {
                            position: "top-center",
                            limit: 1,
                            transition: Zoom,
                            autoClose: 2000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "dark",
                        });
                        return;
                    }

                    set({ MedicineRecommendationLoading: true });

                    const res = await shopModel.post('/api/v1/getDrugFromAIWithParam', {
                        UID: selectedPatientdetails?.UID,
                        chief_complaints: symptoms.map((e) => e?.symptoms || "")
                    });

                    if (res?.status === 200) {

                        const MedicineRecommendationData = res?.data;

                        const parsedData = MedicineRecommendationData.map((e) => ({
                            ...(e?.general || {}),
                            id: e?.productid,
                            medicine: e?.medicine,
                            reason: e?.reason
                        }));

                        set({ medicinerecommendation: parsedData });


                        console.log("Medicine recommendations fetched successfully:", parsedData);
                    }

                } catch (error) {
                    console.error("Error fetching medicine recommendations:", error);
                    toast.error("Something went wrong while fetching recommendations.", {
                        position: "top-center",
                        autoClose: 2000,
                        theme: "dark",
                    });
                } finally {
                    set({ MedicineRecommendationLoading: false });
                }
            },

            getPulseDetails: async (UID) => {
                if (!UID) return;
                try {
                    const res = await profileModel.get(`/GetPulse/${UID}`);
                    if (res.status === 200 && res.data) {
                        const { BPM, spo2 } = res.data;
                        set((state) => ({
                            metrics: {
                                ...state.metrics,
                                pulseRate: Math.round(BPM) || Math.round(state.metrics.pulseRate) || "",
                                oximeterValue: Math.round(spo2) || Math.round(state.metrics.oximeterValue) || "",
                            }
                        }));
                    }
                } catch (err) {
                    console.log("Error fetching pulse details:", err);
                }
            },

            resetPrescriptionState: () =>
                set((state) => {
                    const { selectedPatientdetails, selectedAppointemntdetails } = state;
                    return {
                        selectedPatientdetails,
                        selectedAppointemntdetails,
                        audioBase64: null,
                        selectedPrescription: null,
                        creationStartTime: null,
                        openRecipeAI: false,
                        recipeLoading: false,
                        recipeAIRecommendation: [],
                        selectedRecipes: [],
                        metrics: {
                            bloodPressure: "",
                            randomBloodSugar: "",
                            pulseRate: "",
                            oximeterValue: "",
                            respiratoryRate: "",
                        },
                        formData: {},
                        customMetrics: [],
                        labTests: [],
                        FoodtobeTaken: [],
                        Foodtobeavoided: [],
                        yogalist: {},
                        meditationList: {},
                        pranayama: {},
                        lifestylechanges: "",
                        FollowUp: "No",
                        symptoms: [],
                        diseases: [],
                        lifestylePractices: [],
                        Prakriti_details: null,
                        lifestylePracticesLoading: false,
                        aiParameters: [],
                        doshaAIdiagnois: null,
                        Dosham: {
                            Increased: { doshas: [], percentage: {}, intensity: {} },
                            Decreased: { doshas: [], percentage: {}, intensity: {} },
                            Reason: ""
                        },

                        Ayurvedaparameters: Object.values(ayurvedaAssessments).map(assessment => ({
                            ...assessment,
                            value: '',
                            questions: assessment.questions.map(q => ({ ...q, answer: '' }))
                        })),

                        Medicineinputs: [
                            {
                                medicine: "",
                                Reason: "",
                                dose: "",
                                instruction: "",
                                duration: "",
                                quantity: 1,
                                id: "",
                                timing: "",
                            },
                        ],

                        Medicaldetails: {
                            Complaints: "",
                            lab: "",
                            history: "",
                            drugs: "",
                            ayurvedadiagnosis: "",
                            moderndiagnosis: "",
                            diagnosis: "",
                        },

                        familyHistory: [
                            { relation: 'Father', disease: '', duration: '' },
                            { relation: 'Mother', disease: '', duration: '' }
                        ],

                        currentDrugs: [{
                            medicine: "",
                            dose: "",
                            duration: "",
                        }],
                    };
                }),

            setLabTests: (dataOrUpdater) =>
                set((state) => ({
                    labTests:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.labTests)
                            : dataOrUpdater,
                })),

            setFoodtobetaken: (dataOrUpdater) =>
                set((state) => ({
                    FoodtobeTaken:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.FoodtobeTaken)
                            : dataOrUpdater,
                })),

            setFoodtobeAvoided: (dataOrUpdater) =>
                set((state) => ({
                    Foodtobeavoided:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.Foodtobeavoided)
                            : dataOrUpdater,
                })),

            setyogalist: (obj) => set({ yogalist: obj }),

            setlifestylechanges: (data) => set({ lifestylechanges: data }),

            setfollowup: (data) => set({ FollowUp: data }),

            setMeditationlist: (obj) => set({ meditationList: obj }),

            setSymptoms: (dataOrUpdater) =>
                set((state) => ({
                    symptoms:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.symptoms)
                            : dataOrUpdater,
                })),

            setDiseases: (dataOrUpdater) =>
                set((state) => ({
                    diseases:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.diseases)
                            : dataOrUpdater,
                })),

            setMedicineinputs: (dataOrUpdater) =>
                set((state) => ({
                    Medicineinputs:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.Medicineinputs)
                            : dataOrUpdater,
                })),

            setPranayamaList: (obj) => set({ pranayama: obj }),

            setPatientProfile: (data) => set({ patientProfile: data }),
            setHerbalProducts: (data) => set({ herbalProducts: data }),
            setAllDiet: (data) => set({ AllDiet: data }),
            setAllrawDiet: (data) => set({ AllrawDiet: data }),
            setAllRecipes: (data) => set({ AllRecipes: data }),
            setRecipes: (data) => set({ Recipes: data }),
            setPractices: (data) => set({ Practices: data }),

            setTranscript: (data) => set({ transcript: data }),
            setError: (data) => set({ error: data }),
            setincludedCategories: (data) => set({ includedCategories: data }),
            setavoidedCategories: (data) => set({ avoidedCategories: data }),
            setCreationStartTime: (data) => set({ creationStartTime: data }),

            setRecipeAIRecommendation: (data) =>
                set((state) => {
                    const timings = ["breakfast", "lunch", "snack", "dinner"];
                    const currentRecommendations = state.recipeAIRecommendation || [];

                    const hasAllTimings = timings.every(t =>
                        currentRecommendations.some(r => r.time === t)
                    );

                    return {
                        recipeAIRecommendation: hasAllTimings
                            ? [...data]
                            : [...currentRecommendations, ...data],
                    };
                }),

            setSelectedRecipes: (data) => set({ selectedRecipes: data }),

            handleSelectRecipe: (item, isChecked) => {
                const current = get().selectedRecipes;

                const updated = isChecked
                    ? [...current, { ...item }]
                    : current.filter((i) => i.id !== item.id);
                set({ selectedRecipes: updated });
            },

            setOpenRecipeAI: (val) => set({ openRecipeAI: val }),

            setRecipeLoading: (val) => set({ recipeLoading: val }),


        }),
        {
            name: 'Prescription',
            getStorage: () => sessionStorage,
        }
    )
);

export default usePrescriptionStore;